import React, { useState } from "react"

import { useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import { Color, Size } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import { StyledLink, WithLoadingIndicator } from "~tailwindui/Basics"
import Table from "~tailwindui/Table/Table"

import LocalTime from "components/LocalTime"

export type UnbilledSessionsProps = {
  hideIfEmpty?: boolean
}

const UnbilledSessions: React.FC<UnbilledSessionsProps> = ({
  hideIfEmpty = false,
}) => {
  const history = useHistory()
  const [page, setPage] = useState(1)
  const [result] = useQuery({
    query: unbilledSessionsQuery,
    variables: { page },
  })

  if (
    hideIfEmpty &&
    result.data &&
    result.data.viewer.sessions.data.length === 0
  )
    return null

  return (
    <WithLoadingIndicator
      result={result}
      error="An error occurred while loading your unbilled sessions"
    >
      {({ data: { viewer } }) => (
        <Table
          title="Unbilled Sessions"
          changePage={setPage}
          rowCallback={({ id }) => {
            history.push(path("sessionDetails", { id }))
          }}
          data={viewer.sessions}
          cols={[
            {
              header: "Study Group",
              value: session =>
                session.studyGroup ? (
                  <StyledLink
                    key={session.studyGroup.id}
                    to={path("studyGroupDetails", {
                      id: session.studyGroup.id,
                    })}
                  >
                    <span className="flex items-center">
                      {session.studyGroup.name}
                    </span>
                  </StyledLink>
                ) : null,
            },
            { header: "Subject", value: session => session.subject.name },
            {
              header: "Students",
              value: session => (
                <ul key={session.id}>
                  {session.students.map(student => (
                    <li key={student.id}>
                      <StyledLink
                        to={path("studentDetails", {
                          id: student.id,
                        })}
                      >
                        {student.fullName}
                      </StyledLink>
                    </li>
                  ))}
                </ul>
              ),
            },
            {
              header: "Date",
              value: session => (
                <div key={session.id} className="min-w-[80px]">
                  <LocalTime format="ddd MMM D" timestamp={session.startsAt} />
                  <br />
                  <LocalTime omitDate timestamp={session.startsAt} />
                  &mdash;
                  <LocalTime omitDate timestamp={session.endsAt} />
                </div>
              ),
            },
            {
              header: "Estimated Duration",
              value: session => session.formattedScheduledDuration,
            },
            {
              header: "Actions",
              value: session => (
                <StyledLink
                  button
                  color={Color.Teal}
                  size={Size.Small}
                  to={path("sessionBill", { id: session.id })}
                >
                  Complete
                </StyledLink>
              ),
            },
          ]}
        />
      )}
    </WithLoadingIndicator>
  )
}

const unbilledSessionsQuery = gql`
  query UnbilledSessions($page: Int!) {
    viewer {
      sessions(
        filters: { past: true, billState: unbilled, statusState: scheduled }
        page: $page
      ) {
        currentPage
        totalPages
        data {
          id
          startsAt
          endsAt
          formattedScheduledDuration
          students {
            id
            fullName
          }
          subject {
            name
          }
          studyGroup {
            id
            name
          }
        }
      }
    }
  }
`

export default UnbilledSessions
